// Pure resolution logic for the Amplitude Experiment "Variant Container"
// content type (Contentful content type id `variantContainer`, GraphQL type
// `VariantContainer`).
//
// The container carries:
//  - `experimentId`: the Amplitude flag key to evaluate
//  - `meta`: a JSON map of `variant key -> variation entry id`
//  - `variantsCollection`: the actual entries, one per variant
//
// Keeping this free of React/analytics imports lets it be unit tested and lets
// the layout code unwrap containers without pulling in the renderer.

export const VARIANT_CONTAINER_TYPENAME = "VariantContainer";

// Amplitude's convention for the baseline variant, and what the SDK falls back
// to when the flag is off or the user isn't bucketed.
export const CONTROL_VARIANT_KEY = "control";

export type VariantEntry = {
  __typename?: string | null;
  sys?: { id?: string | null } | null;
  [key: string]: unknown;
};

export type VariantContainerEntry = {
  __typename?: string | null;
  experimentId?: string | null;
  meta?: Record<string, string> | null;
  variantsCollection?: { items: (VariantEntry | null)[] } | null;
};

// The container's GraphQL selection only pulls the fields of the component
// types listed here — the same ones a page body selects — so anything else an
// editor links would render as an empty component. Ignore those and keep the
// control variant instead. Extend this list together with the fragment in
// `ContentfulVariantContainer`.
export const SUPPORTED_VARIANT_TYPENAMES = [
  "Component",
  "ComponentBenefits",
  "ComponentCards",
  "ComponentFaq",
  "ComponentHero",
  "ComponentPackages",
  "ComponentPromoBanner",
  "ComponentRichText",
  "ComponentStructuredData",
  "ComponentTextAndList",
  "ComponentTextAndMedia",
  "ComponentTextAndMediaCarousel",
  "ComponentTextAndText",
];

export const isVariantContainer = (
  component: unknown
): component is VariantContainerEntry =>
  !!component &&
  typeof component === "object" &&
  (component as VariantContainerEntry).__typename ===
    VARIANT_CONTAINER_TYPENAME;

const variantEntries = (container: VariantContainerEntry): VariantEntry[] =>
  (container.variantsCollection?.items ?? []).flatMap((item) =>
    item && SUPPORTED_VARIANT_TYPENAMES.includes(item.__typename ?? "")
      ? [item]
      : []
  );

const byId = (entries: VariantEntry[], id: string | undefined | null) =>
  id ? entries.find((entry) => entry.sys?.id === id) : undefined;

/**
 * The entry rendered before Amplitude has answered (and in the statically
 * generated HTML): the variant `meta` maps to `control`, else the first entry
 * the editor linked.
 */
export const fallbackVariant = (
  container: VariantContainerEntry
): VariantEntry | null => {
  const entries = variantEntries(container);
  return (
    byId(entries, container.meta?.[CONTROL_VARIANT_KEY]) ?? entries[0] ?? null
  );
};

/**
 * The entry for the variant key Amplitude assigned. Falls back to the control
 * entry for `off`, for an unknown key, or before the SDK has resolved — a
 * misconfigured experiment must never blank out the page.
 */
export const resolveVariant = (
  container: VariantContainerEntry,
  variantKey: string | undefined | null
): VariantEntry | null => {
  const entries = variantEntries(container);

  if (entries.length === 0) {
    return null;
  }

  const assigned = variantKey
    ? byId(entries, container.meta?.[variantKey])
    : undefined;

  return assigned ?? fallbackVariant(container);
};

/**
 * Runs a page's per-component decoration (the FAQ block's tag items, fetched
 * after the page query) over every variant, so a decorated component behaves
 * the same inside a container as outside one. Non-containers pass through.
 */
export const decorateVariants = async <T>(
  component: T,
  decorate: (entry: VariantEntry) => Promise<VariantEntry>
): Promise<T> => {
  if (!isVariantContainer(component) || !component.variantsCollection) {
    return component;
  }
  const items = await Promise.all(
    component.variantsCollection.items.map((item) =>
      item ? decorate(item) : item
    )
  );
  return {
    ...component,
    variantsCollection: { ...component.variantsCollection, items },
  };
};

/**
 * Layout decisions (does this page open with a hero? which gutter follows this
 * section?) run on the server against a stable shape, so they read the control
 * variant rather than the container wrapper. Non-containers pass through.
 */
export const unwrapVariantContainer = <T>(component: T): T | VariantEntry =>
  isVariantContainer(component)
    ? fallbackVariant(component) ?? component
    : component;
